import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Minus, Users, ShoppingCart, DollarSign, Package, AlertTriangle, RefreshCw, Info } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, Cell } from "recharts";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";

// --- helpers -----------------------------------------------------------------

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString("es-EC", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtInt = (n) => Number(n || 0).toLocaleString("es-EC");

const STATUS_LABEL = {
  PENDIENTE_PAGO: "Pendiente de pago",
  PAGO_POR_VERIFICAR: "Transferencia por verificar",
  PAGADO: "Pagado",
  ENVIADO: "Enviado",
  ENTREGADO: "Entregado",
  PENDIENTE_LIBERACION: "Pendiente liberación",
  LIBERADO: "Liberado",
  EN_DISPUTA: "En disputa",
  RESUELTA_PARCIAL: "Resuelta parcial",
  REEMBOLSADO: "Reembolsado",
  CANCELADO: "Cancelado",
  RECHAZADO: "Rechazado",
};

const METHOD_LABEL = {
  payphone: "Tarjeta (Payphone)",
  transfer: "Transferencia bancaria",
  unknown: "Sin definir",
};

function DeltaChip({ pct }) {
  if (pct === null || pct === undefined) {
    return <span className="inline-flex items-center gap-1 text-xs text-neutral-500"><Minus size={11} /> s/ref</span>;
  }
  const up = pct > 0;
  const flat = pct === 0;
  const cls = flat ? "text-neutral-400" : up ? "text-emerald-400" : "text-red-400";
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${cls}`} data-testid="analytics-delta">
      <Icon size={11} />{pct > 0 ? "+" : ""}{pct}% vs anterior
    </span>
  );
}

/**
 * Chip vivo con el estado de TCGdex (fuente externa del catálogo Pokémon).
 * Feb 2026: cuando la API pública `api.tcgdex.net` se cae, los vendedores
 * ven cero cartas al publicar. Este chip le avisa al admin en el instante
 * en que pasa, para no enterarse "de casualidad".
 * Ping ligero cada 60s al endpoint solo-lectura `/admin/tcgdex-health` que
 * devuelve el snapshot en memoria (no dispara HTTP externo).
 */
function TcgdexHealthChip() {
  const [health, setHealth] = useState(null);
  useEffect(() => {
    let mounted = true;
    const load = () => {
      api.get("/admin/tcgdex-health")
        .then((r) => { if (mounted) setHealth(r.data); })
        .catch(() => { if (mounted) setHealth({ ok: false, last_error_kind: "fetch_error" }); });
    };
    load();
    const t = setInterval(load, 60000);
    return () => { mounted = false; clearInterval(t); };
  }, []);
  if (!health) return null;
  const ok = !!health.ok;
  const cls = ok
    ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
    : "bg-red-500/10 border-red-500/40 text-red-300";
  const lastOk = health.last_ok_at
    ? new Date(health.last_ok_at).toLocaleTimeString("es-EC", { hour: "2-digit", minute: "2-digit" })
    : "—";
  const title = ok
    ? `TCGdex responde bien. Última respuesta OK: ${lastOk}.`
    : `TCGdex no responde. Última respuesta OK: ${lastOk}. Último error: ${health.last_error_kind || "desconocido"}. Los vendedores verán el banner rojo al publicar.`;
  return (
    <div
      data-testid={`tcgdex-health-chip-${ok ? "ok" : "down"}`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] ${cls}`}
      title={title}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${ok ? "bg-emerald-400" : "bg-red-400 animate-pulse"}`} />
      TCGdex: {ok ? "OK" : "Caído"}
      {!ok && health.last_ok_at && <span className="opacity-60">· últ. OK {lastOk}</span>}
    </div>
  );
}

/**
 * Chip del espejo local Pokémon (Feb 2026). Muestra cuántas cartas hay
 * cacheadas y hace cuánto se refrescó. Botón "Refrescar" dispara el job en
 * background (mismo endpoint que el cron nocturno). Cero mutación de datos
 * de negocio.
 */
function PokemonMirrorChip() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/pokemon-mirror/status")
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ enabled: false }));
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const refresh = async () => {
    setBusy(true);
    try {
      await api.post("/admin/pokemon-mirror/refresh");
      toast.success("Refresh del espejo Pokémon en marcha (tarda unos minutos)");
      setTimeout(load, 3000);
    } catch (e) {
      toast.error("No se pudo iniciar el refresh");
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;
  const count = status.card_count || 0;
  const enabled = !!status.enabled;
  const refreshing = !!status.refreshing;

  let ageMin = null;
  if (status.last_ok_at) {
    ageMin = Math.round((Date.now() - new Date(status.last_ok_at).getTime()) / 60000);
  }
  const ageText = ageMin == null
    ? "sin sync"
    : ageMin < 60
      ? `hace ${ageMin} min`
      : ageMin < 1440
        ? `hace ${Math.round(ageMin / 60)} h`
        : `hace ${Math.round(ageMin / 1440)} d`;

  const cls = enabled
    ? "bg-blue-500/10 border-blue-500/30 text-blue-300"
    : "bg-neutral-500/10 border-neutral-500/30 text-neutral-400";

  return (
    <div
      data-testid={`pokemon-mirror-chip-${enabled ? "on" : "off"}`}
      className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-[11px] ${cls}`}
      title={enabled ? `Espejo Pokémon: ${count} cartas, sync ${ageText}` : "Espejo vacío — ejecuta un refresh"}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
      Espejo Pokémon: {enabled ? `${count.toLocaleString("es-EC")} cartas · ${ageText}` : "vacío"}
      {refreshing && <span className="opacity-70">· sincronizando…</span>}
      <button
        type="button"
        onClick={refresh}
        disabled={busy || refreshing}
        data-testid="pokemon-mirror-refresh-btn"
        className="ml-1 px-1.5 py-0.5 rounded bg-blue-500/20 hover:bg-blue-500/40 border border-blue-500/40 text-[10px] disabled:opacity-40"
      >
        {busy ? "…" : "Refrescar"}
      </button>
    </div>
  );
}


function KpiCard({ label, value, delta, hint, testid, tone = "default" }) {
  const toneClass = {
    default: "border-[#262626]",
    money: "border-emerald-500/30 bg-emerald-500/5",
    warn: "border-amber-500/30 bg-amber-500/5",
  }[tone];
  return (
    <div className={`bg-[#111] border ${toneClass} rounded-md p-4`} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-neutral-500 flex items-center gap-1">
        {label}
        {hint && <span title={hint}><Info size={11} className="text-neutral-500" /></span>}
      </div>
      <div className="mt-1 font-display text-2xl md:text-3xl text-white">{value}</div>
      {delta !== undefined && <div className="mt-1"><DeltaChip pct={delta} /></div>}
    </div>
  );
}


/**
 * Panel unificado del estado de todas las APIs externas críticas.
 * Consume `/admin/apis-health` (solo lectura, sin llamadas nuevas hacia
 * afuera). Refresh cada 60s. Grid compacto de chips con color según estado:
 *   ok=true → verde   ok=false → rojo pulsante   ok=null → gris "sin data"
 */
function ApiChip({ apiKey, snap }) {
  const ok = snap.ok;
  const cls = ok === true
    ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
    : ok === false
      ? "bg-red-500/10 border-red-500/40 text-red-300"
      : "bg-neutral-500/10 border-neutral-500/30 text-neutral-400";
  const dotCls = ok === true
    ? "bg-emerald-400"
    : ok === false
      ? "bg-red-400 animate-pulse"
      : "bg-neutral-500";
  const stateLabel = ok === true ? "Arriba" : ok === false ? "Caído" : "Sin actividad registrada";
  const lastOk = snap.last_ok_at
    ? new Date(snap.last_ok_at).toLocaleTimeString("es-EC", { hour: "2-digit", minute: "2-digit" })
    : null;
  const detail = snap.detail || {};

  // Detalle contextual por API
  let extra = null;
  if (apiKey === "payphone") {
    extra = detail.mode && detail.mode !== "mock" ? `modo LIVE (${detail.mode})` : "modo mock";
  } else if (apiKey === "resend") {
    extra = detail.has_api_key ? `${detail.total_ok || 0} OK / ${detail.total_fail || 0} fallos` : "sin API key";
  } else if (apiKey === "pokemontcg_io") {
    if (detail.breaker_open) extra = "circuit breaker abierto";
    else extra = `${detail.total_requests || 0} req / ${detail.total_failures || 0} fallos`;
  } else if (apiKey === "tcgdex") {
    if (snap.last_error_kind) extra = snap.last_error_kind;
  } else if (apiKey === "tcgdex_cdn" || apiKey === "pokemontcg_cdn") {
    // Chips de CDN: mostrar HTTP status del último HEAD activo.
    if (detail.status) extra = `HTTP ${detail.status}`;
    else if (detail.error) extra = `error: ${detail.error}`;
  }

  // Feb 2026 — "Sin actividad registrada" es DISTINTO de "Caído".
  // El chip gris significa que no hubo tráfico suficiente para medir stats
  // (payphone/resend). NO significa que la API esté abajo.
  const title = [
    `${snap.label}: ${stateLabel}`,
    ok === null ? "(no hay estadística en el período — no significa que esté caído)" : null,
    lastOk ? `Último OK: ${lastOk}` : null,
    snap.last_error_kind ? `Último error: ${snap.last_error_kind}` : null,
  ].filter(Boolean).join(" · ");

  return (
    <div
      data-testid={`api-health-chip-${apiKey}`}
      className={`flex items-center gap-2 rounded-md border px-3 py-2 text-xs ${cls}`}
      title={title}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dotCls} shrink-0`} />
      <div className="min-w-0 flex-1">
        <div className="font-medium truncate">{snap.label}</div>
        <div className="opacity-70 truncate text-[10px]">
          {stateLabel}{lastOk ? ` · últ. OK ${lastOk}` : ""}{extra ? ` · ${extra}` : ""}
        </div>
      </div>
    </div>
  );
}

function ApisHealthPanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let mounted = true;
    const load = () => {
      api.get("/admin/apis-health")
        .then((r) => { if (mounted) setData(r.data); })
        .catch(() => { /* silencio — chip principal ya cubre TCGdex */ });
    };
    load();
    const t = setInterval(load, 60000);
    return () => { mounted = false; clearInterval(t); };
  }, []);
  if (!data?.apis) return null;
  // Feb 2026 — orden: TCGdex API + CDN separados (arriba), pokemontcg
  // API + CDN separados (fallback), Payphone + Resend (servicios de negocio).
  const order = ["tcgdex", "tcgdex_cdn", "pokemontcg_io", "pokemontcg_cdn", "payphone", "resend"];
  return (
    <div className="w-full" data-testid="apis-health-panel">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mt-2">
        {order.map((k) => data.apis[k] ? <ApiChip key={k} apiKey={k} snap={data.apis[k]} /> : null)}
      </div>
    </div>
  );
}


// --- main page ---------------------------------------------------------------

export default function AdminAnalytics() {
  const [range, setRange] = useState(30);
  const [includeTest, setIncludeTest] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.get("/admin/analytics/overview", { params: { range_days: range, include_test: includeTest } });
      setData(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "No se pudo cargar el panel";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range, includeTest]);

  if (loading && !data) return <div className="p-6 text-neutral-400" data-testid="analytics-loading">Cargando métricas…</div>;
  if (err && !data) return <div className="p-6 text-red-400" data-testid="analytics-error">Error: {err}</div>;
  if (!data) return null;

  const { users, business, catalog, health } = data;

  return (
    <div className="space-y-6" data-testid="admin-analytics-page">
      {/* Header + controles */}
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h2 className="font-display text-2xl">Analytics interno</h2>
          <div className="text-xs text-neutral-500">Solo lectura · Datos generados {new Date(data.generated_at).toLocaleString("es-EC")}</div>
        </div>
        <TcgdexHealthChip />
        <PokemonMirrorChip />
        <div className="ml-auto flex items-center gap-3 flex-wrap">
          <div className="flex bg-[#111] border border-[#262626] rounded-md overflow-hidden" data-testid="analytics-range-selector">
            {[7, 30, 90].map(d => (
              <button
                key={d}
                data-testid={`analytics-range-${d}`}
                onClick={() => setRange(d)}
                className={`px-3 py-1.5 text-sm ${range === d ? "bg-[#EF4444] text-white" : "text-neutral-400 hover:text-white"}`}
              >{d}d</button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-xs text-neutral-300 bg-[#111] border border-[#262626] rounded-md px-3 py-1.5" data-testid="analytics-test-toggle-wrap">
            <Switch data-testid="analytics-test-toggle" checked={includeTest} onCheckedChange={setIncludeTest} />
            Incluir cuentas de prueba
          </label>
          <Button size="sm" variant="outline" onClick={load} className="border-[#262626]" data-testid="analytics-refresh">
            <RefreshCw size={14} className="mr-1" />Refrescar
          </Button>
        </div>
      </div>

      {/* Estado de APIs externas — grid completo debajo del header */}
      <ApisHealthPanel />


      {/* Aviso de datos no disponibles */}
      {data.conversion === null && (
        <div className="bg-[#111] border border-amber-500/30 rounded-md p-3 text-xs text-amber-300 flex items-start gap-2" data-testid="analytics-conversion-notice">
          <Info size={14} className="mt-0.5 shrink-0" />
          <div>
            <b>Tasa de conversión no disponible.</b> Hoy no se registran las visitas al catálogo, solo eventos de login y órdenes. Si quieres medir "de los que ven, cuántos compran", pídeme agregar un contador de visitas — es un cambio aparte.
          </div>
        </div>
      )}

      {/* -------- BLOQUE 1: Negocio (lo más importante) -------- */}
      <section data-testid="analytics-business-section">
        <h3 className="font-display text-lg mb-3 flex items-center gap-2"><DollarSign size={18} className="text-emerald-400" />Negocio · ventana de {range} días</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard
            testid="kpi-gmv-range"
            label="GMV (volumen de ventas)"
            value={fmtUSD(business.concretadas.range.gmv)}
            delta={business.concretadas.delta_pct.gmv}
            hint="Suma del total de órdenes CONCRETADAS creadas en la ventana. Excluye canceladas/reembolsadas."
            tone="money"
          />
          <KpiCard
            testid="kpi-commission-range"
            label="Comisión HitTCG"
            value={fmtUSD(business.concretadas.range.commission)}
            delta={business.concretadas.delta_pct.commission}
            hint="Comisión generada sobre las órdenes concretadas en la ventana. Ojo: puede estar en escrow hasta que se liberen."
            tone="money"
          />
          <KpiCard
            testid="kpi-orders-concretadas-range"
            label="Órdenes concretadas"
            value={fmtInt(business.concretadas.range.count)}
            delta={business.concretadas.delta_pct.count}
            hint="Órdenes con status PAGADO en adelante (incluye escrow abierto). Refleja la plata comprometida."
          />
          <KpiCard
            testid="kpi-ticket-avg"
            label="Ticket promedio (histórico)"
            value={fmtUSD(business.concretadas.all_time.ticket_avg)}
            hint="GMV histórico / número de órdenes concretadas histórico."
          />
        </div>

        {/* Cerradas — bloque secundario */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <KpiCard
            testid="kpi-cerradas-count-range"
            label="Órdenes cerradas (ciclo completo)"
            value={fmtInt(business.cerradas.range.count)}
            delta={business.cerradas.delta_pct.count}
            hint="Órdenes LIBERADAS en la ventana — plata que realmente salió al vendedor."
          />
          <KpiCard
            testid="kpi-cerradas-gmv-range"
            label="GMV cerrado"
            value={fmtUSD(business.cerradas.range.gmv)}
            delta={business.cerradas.delta_pct.gmv}
            hint="Volumen de las órdenes que cerraron ciclo en la ventana."
          />
          <KpiCard
            testid="kpi-cerradas-commission-range"
            label="Comisión cobrada (ya liberada)"
            value={fmtUSD(business.cerradas.range.commission)}
            delta={business.cerradas.delta_pct.commission}
            hint="Comisión de órdenes ya liberadas — ingreso definitivo, ya no en escrow."
            tone="money"
          />
          <KpiCard
            testid="kpi-gmv-alltime"
            label="GMV histórico total"
            value={fmtUSD(business.concretadas.all_time.gmv)}
            hint="Suma de todas las órdenes concretadas desde el arranque de la plataforma."
          />
        </div>
      </section>

      {/* -------- BLOQUE 2: Actividad (charts) -------- */}
      <section data-testid="analytics-activity-section">
        <h3 className="font-display text-lg mb-3 flex items-center gap-2"><TrendingUp size={18} className="text-purple-400" />Actividad · últimas 12 semanas</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <MiniChart
            testid="chart-orders-weekly"
            title="Órdenes concretadas por semana"
            data={business.orders_by_week}
            dataKey="count"
            color="#EF4444"
            formatValue={fmtInt}
          />
          <MiniChart
            testid="chart-gmv-weekly"
            title="GMV por semana"
            data={business.orders_by_week}
            dataKey="gmv"
            color="#22c55e"
            formatValue={fmtUSD}
          />
          <MiniChart
            testid="chart-pubs-weekly"
            title="Publicaciones nuevas por semana"
            data={catalog.pubs_by_week}
            dataKey="count"
            color="#a855f7"
            formatValue={fmtInt}
          />
        </div>
      </section>

      {/* -------- BLOQUE 3: Usuarios -------- */}
      <section data-testid="analytics-users-section">
        <h3 className="font-display text-lg mb-3 flex items-center gap-2"><Users size={18} className="text-blue-400" />Usuarios</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard testid="kpi-users-total" label="Total registrados" value={fmtInt(users.total)} hint="Total de cuentas registradas." />
          <KpiCard testid="kpi-users-active-7d" label="Activos últimos 7 días" value={fmtInt(users.active_7d)} hint="Usuarios únicos con evento de login en los últimos 7 días. Proxy: no medimos visitas anónimas." />
          <KpiCard testid="kpi-users-active-30d" label="Activos últimos 30 días" value={fmtInt(users.active_30d)} hint="Usuarios únicos con evento de login en los últimos 30 días." />
          <KpiCard testid="kpi-users-new-range" label={`Nuevos en ${range} días`} value={fmtInt(users.new_in_range)} delta={users.new_delta_pct} hint="Cuentas creadas dentro de la ventana." />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <KpiCard testid="kpi-users-sellers" label="Son vendedores" value={fmtInt(users.sellers)} hint="Publicaron aprobadas o vendieron al menos una orden." />
          <KpiCard testid="kpi-users-buyers-only" label="Solo compradores" value={fmtInt(users.buyers_only)} hint="Compraron pero nunca han vendido." />
          <KpiCard testid="kpi-users-inactive" label="Sin actividad" value={fmtInt(users.inactive)} hint="Registrados pero nunca compraron ni vendieron." />
          <KpiCard testid="kpi-users-verified" label="Verificados" value={`${fmtInt(users.verified)} / ${fmtInt(users.total)}`} hint="Cuentas con cédula aprobada." />
        </div>
        <div className="bg-[#111] border border-[#262626] rounded-md p-4 mt-3" data-testid="chart-users-weekly">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">Nuevos usuarios por semana (12 semanas)</div>
          <div style={{ width: "100%", height: 140 }}>
            <ResponsiveContainer>
              <LineChart data={users.by_week}>
                <CartesianGrid stroke="#262626" strokeDasharray="3 3" />
                <XAxis dataKey="week_start" stroke="#666" tick={{ fontSize: 10 }} />
                <YAxis stroke="#666" tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #262626", fontSize: 12 }} />
                <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* -------- BLOQUE 4: Catálogo -------- */}
      <section data-testid="analytics-catalog-section">
        <h3 className="font-display text-lg mb-3 flex items-center gap-2"><Package size={18} className="text-orange-400" />Catálogo</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard testid="kpi-catalog-pubs-total" label="Publicaciones activas" value={fmtInt(catalog.active_publications)} hint="Publicaciones con status ACTIVE en la colección publications." />
          <KpiCard testid="kpi-catalog-pubs-pokemon" label="Pokémon activas" value={fmtInt(catalog.active_publications_by_game.pokemon)} />
          <KpiCard testid="kpi-catalog-pubs-riftbound" label="Riftbound activas" value={fmtInt(catalog.active_publications_by_game.riftbound)} />
          <KpiCard testid="kpi-catalog-unique-cards" label="Cartas únicas en catálogo" value={fmtInt(catalog.unique_cards)} hint={`Pokémon: ${catalog.unique_cards_by_game.pokemon} · Riftbound: ${catalog.unique_cards_by_game.riftbound}`} />
        </div>
      </section>

      {/* -------- BLOQUE 5: Órdenes por estado + método -------- */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="analytics-orders-breakdown">
        <div className="bg-[#111] border border-[#262626] rounded-md p-4" data-testid="orders-by-status">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-3">Órdenes por estado (histórico)</div>
          <div className="space-y-1.5">
            {Object.entries(business.by_status).sort((a, b) => b[1] - a[1]).map(([st, cnt]) => (
              <div key={st} className="flex justify-between items-center text-sm" data-testid={`status-row-${st}`}>
                <span className="text-neutral-300">{STATUS_LABEL[st] || st}</span>
                <span className="text-white font-mono">{fmtInt(cnt)}</span>
              </div>
            ))}
            {Object.keys(business.by_status).length === 0 && <div className="text-neutral-500 text-sm">Sin órdenes todavía.</div>}
          </div>
        </div>

        <div className="bg-[#111] border border-[#262626] rounded-md p-4" data-testid="orders-by-payment-method">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-3">Método de pago · órdenes concretadas</div>
          <div className="space-y-1.5">
            {Object.entries(business.by_payment_method).map(([m, v]) => (
              <div key={m} className="flex justify-between items-center text-sm" data-testid={`method-row-${m}`}>
                <span className="text-neutral-300">{METHOD_LABEL[m] || m}</span>
                <span className="text-white font-mono">{fmtInt(v.count)} · {fmtUSD(v.gmv)}</span>
              </div>
            ))}
            {Object.keys(business.by_payment_method).length === 0 && <div className="text-neutral-500 text-sm">Sin órdenes todavía.</div>}
          </div>
        </div>
      </section>

      {/* -------- BLOQUE 6: Salud del marketplace -------- */}
      <section data-testid="analytics-health-section">
        <h3 className="font-display text-lg mb-3 flex items-center gap-2"><AlertTriangle size={18} className="text-amber-400" />Salud del marketplace</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard testid="kpi-health-refunds" label="Reembolsos abiertos" value={fmtInt(health.refunds_open)} tone={health.refunds_open > 0 ? "warn" : "default"} />
          <KpiCard testid="kpi-health-disputes" label="Disputas abiertas" value={fmtInt(health.disputes_open)} tone={health.disputes_open > 0 ? "warn" : "default"} />
          <KpiCard testid="kpi-health-strikes" label="Strikes activos" value={fmtInt(health.strikes_active)} />
          <KpiCard testid="kpi-health-debts" label="Deudas de vendedor" value={fmtInt(health.seller_debts_pending)} tone={health.seller_debts_pending > 0 ? "warn" : "default"} />
        </div>
      </section>

      <div className="text-[11px] text-neutral-600 text-center pt-6 pb-2">
        Panel solo lectura · No modifica órdenes ni saldos · Cero mutación de datos
      </div>
    </div>
  );
}

function MiniChart({ title, data, dataKey, color, formatValue, testid }) {
  return (
    <div className="bg-[#111] border border-[#262626] rounded-md p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1">{title}</div>
      <div className="text-white font-display text-lg mb-2">{data.length > 0 ? formatValue(data[data.length - 1]?.[dataKey]) : "—"} <span className="text-xs text-neutral-500">(última sem.)</span></div>
      <div style={{ width: "100%", height: 100 }}>
        <ResponsiveContainer>
          <BarChart data={data}>
            <XAxis dataKey="week_start" stroke="#444" tick={{ fontSize: 9 }} interval={2} />
            <YAxis hide />
            <Tooltip
              contentStyle={{ background: "#0A0A0A", border: "1px solid #262626", fontSize: 11 }}
              formatter={(v) => formatValue(v)}
            />
            <Bar dataKey={dataKey} fill={color}>
              {data.map((_, i) => <Cell key={i} fill={color} fillOpacity={i === data.length - 1 ? 1 : 0.55} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
